import axios from 'axios'
import { pluckData } from 'src/utilities/http-client'
import {
  ImageUploadRequest,
  LinkedService,
  QuestionData,
  QuizV2,
  Reference,
  Section,
  Template,
} from 'src/v3/core/types'
import {
  CREATE_ACCREDITATIONS_API_URL,
  CREATE_CATEGORY_API_URL,
  CREATE_COURSE_V3_API_URL,
  CREATE_INSTRUCTOR_API_URL,
  CREATE_MIGRATE_API_URL,
  CREATE_SERIES_API_URL,
  CREATE_SHOW_NOTES_API_URL,
  CREATE_STREAM_V3_API_URL,
  CREATE_SYLLABUS_API_URL,
  CREATE_TEMPLATE_API_URL,
  CREATE_VIDEO_API_URL,
  CREATE_QUIZ_API_URL,
  contentTypeApiUrlMap,
} from '../constants'
import {
  CoursePayload,
  CoreInfoPayload,
  SyllabusPayload,
  CategoryPayload,
  CategoryResponse,
  TemplatePayload,
  CreateTemplateResponse,
  CourseV5Details,
  CoursePublishedStatus,
  LightweightCourseV5,
  ObjectivesReference,
  GetCourseV5Response,
  MigrateContentPayload,
  CreateVideoPayload,
  CreateVideoResponse,
  VideoPayload,
  VideoUploadUrlsResponse,
  CompleteVideoUploadPayload,
  VideoLibraryItem,
  ShowNotesLibraryItem,
  ShowNotesPayload,
  QuizLibraryItem,
  CreateContentTypes,
  CourseAccreditationResponse,
  CourseAccreditationType,
  InstructorWithCourses,
} from '../course/types'
import {
  SeriesResponse,
  SeriesPayload,
  SeriesImageType,
  UpdateSeriesImagePayload,
} from '../series/types'

// Courses

export let getCourses = () =>
  axios
    .get<LightweightCourseV5[]>(`${CREATE_COURSE_V3_API_URL}s`)
    .then(pluckData)

export let getCourseByID = (id: string) =>
  axios
    .get<GetCourseV5Response>(`${CREATE_COURSE_V3_API_URL}/${id}`)
    .then(pluckData)

export let getLinkedServices = () =>
  axios.get<LinkedService[]>('/api/v2/create/linked-services').then(pluckData)

export let createCourseByLinkedServiceID = (
  id: string,
  payload: CoursePayload,
) =>
  axios
    .post<CourseV5Details>(
      `${CREATE_COURSE_V3_API_URL}/linked-service/${id}`,
      payload,
    )
    .then(pluckData)

export let getCourseImageUploadUrl = (
  request: ImageUploadRequest,
): Promise<{ url: string }> =>
  axios
    .put<string>(`${CREATE_COURSE_V3_API_URL}/image`, {
      id: request.id,
      type: request.type,
      contentType: request.contentType || 'image/jpg',
    })
    .then(pluckData)
    .then((url) => ({ url }))
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to get course image upload URL:', error)
      return Promise.reject(error)
    })

export let updateCourseCore = (id: string, payload: CoreInfoPayload) =>
  axios
    .patch<CourseV5Details>(`${CREATE_COURSE_V3_API_URL}/${id}/core`, payload)
    .then(pluckData)

export let deleteCourse = (id: string) =>
  axios.delete(`${CREATE_COURSE_V3_API_URL}/${id}`)

export let updateCoursePublicationStatus = (
  id: string,
  newPublicationStatus: CoursePublishedStatus,
) =>
  axios
    .put(`/api/v3/create/course/${id}/publish`, { newPublicationStatus })
    .then(pluckData)

// Categories
export let getCategories = () =>
  axios
    .get<Record<string, Section[]>>(`${CREATE_CATEGORY_API_URL}s`)
    .then(pluckData)

export let createCategory = (
  linkedServiceID: string,
  payload: CategoryPayload,
) =>
  axios
    .post<CategoryResponse>(
      `${CREATE_CATEGORY_API_URL}/linked-service/${linkedServiceID}`,
      payload,
    )
    .then(pluckData)

export let updateCategory = (categoryID: string, payload: CategoryPayload) =>
  axios
    .put<CategoryResponse>(`${CREATE_CATEGORY_API_URL}/${categoryID}`, payload)
    .then(pluckData)

// Templates
export let getTemplates = () =>
  axios
    .get<Record<string, Template[]>>(`${CREATE_TEMPLATE_API_URL}s`)
    .then(pluckData)

export let createTemplate = (id: string, payload: TemplatePayload) =>
  axios
    .post<CreateTemplateResponse>(
      `${CREATE_TEMPLATE_API_URL}/linked-service/${id}`,
      payload,
    )
    .then(pluckData)

export let updateTemplate = (id: string, payload: TemplatePayload) =>
  axios
    .put<CreateTemplateResponse>(`${CREATE_TEMPLATE_API_URL}/${id}`, payload)
    .then(pluckData)

// Syllabus
export let getReferences = () =>
  axios
    .get<Record<string, Reference[]>>(`${CREATE_SYLLABUS_API_URL}/references`)
    .then(pluckData)

export let updateCourseSyllabus = (id: string, payload: SyllabusPayload) =>
  axios
    .patch<CourseV5Details>(
      `${CREATE_COURSE_V3_API_URL}/${id}/syllabus`,
      payload,
    )
    .then(pluckData)

// Update library reference text
export let updateReference = (ref: ObjectivesReference) =>
  axios.patch(`${CREATE_SYLLABUS_API_URL}/reference`, ref).then(pluckData)

export let updateCourseSteps = (
  courseID: string,
  steps: { videoID?: string; showNotesID?: string; quizID?: string }[],
) =>
  axios
    .post(`${CREATE_STREAM_V3_API_URL}/course/${courseID}`, {
      steps: steps.map((step) => {
        if (step.videoID) return { video: { videoID: step.videoID } }
        if (step.showNotesID)
          return { showNotes: { showNotesID: step.showNotesID } }
        if (step.quizID) return { quiz: { quizID: step.quizID } }
        return null
      }),
    })
    .then(pluckData)

export let getVideoLibrary = () =>
  axios
    .get<VideoLibraryItem[]>(`${CREATE_VIDEO_API_URL}-library`)
    .then(pluckData)

export let getCourseStepsByCourseID = (courseID: string) =>
  axios.get(`${CREATE_STREAM_V3_API_URL}/course/${courseID}`).then(pluckData)

// Videos (V5)
export let createVideo = (courseID: string, payload: CreateVideoPayload) =>
  axios
    .post<CreateVideoResponse>(
      `${CREATE_VIDEO_API_URL}/course/${courseID}`,
      payload,
    )
    .then(pluckData)

export let updateVideo = (videoID: string, payload: VideoPayload) =>
  axios
    .put<CreateVideoResponse>(`${CREATE_VIDEO_API_URL}/${videoID}`, payload)
    .then(pluckData)

export let getVideoUploadUrls = (numberOfParts: number) =>
  axios
    .get<VideoUploadUrlsResponse>(
      `/api/video-multipart-urls?numberOfParts=${numberOfParts}`,
    )
    .then(pluckData)

export let completeVideoUpload = (payload: CompleteVideoUploadPayload) =>
  axios.post('/api/complete-video-multipart-upload', payload).then(pluckData)

export let linkVideoFile = (videoID: string, key: string) =>
  axios.put(`${CREATE_VIDEO_API_URL}/${videoID}/file`, { key }).then(pluckData)

export let deleteVideo = (videoID: string) =>
  axios.delete(`${CREATE_VIDEO_API_URL}/${videoID}`)

export let createVideoForLinkedService = (
  linkedServiceID: string,
  payload: { name: string; description: string; key: string },
) =>
  axios
    .post<CreateVideoResponse>(
      `${CREATE_VIDEO_API_URL}/linked-service/${linkedServiceID}`,
      payload,
    )
    .then(pluckData)

export let migrateVideo = (payload: MigrateContentPayload) =>
  axios.post(`${CREATE_MIGRATE_API_URL}/video`, payload).then(pluckData)

export let migrateShowNotes = (payload: MigrateContentPayload) =>
  axios.post(`${CREATE_MIGRATE_API_URL}/show-notes`, payload).then(pluckData)

export let migrateQuiz = (payload: MigrateContentPayload) =>
  axios.post(`${CREATE_MIGRATE_API_URL}/quiz`, payload).then(pluckData)

export let migrateAccreditation = (accreditationID: string) =>
  axios.post(`${CREATE_MIGRATE_API_URL}/accreditation/${accreditationID}`)

// Show Notes (V5 Library)
export let getShowNotesLibrary = () =>
  axios
    .get<ShowNotesLibraryItem[]>(`${CREATE_SHOW_NOTES_API_URL}-library`)
    .then(pluckData)

export let createShowNotesForCourse = (
  courseID: string,
  payload: ShowNotesPayload,
) =>
  axios
    .post(`${CREATE_SHOW_NOTES_API_URL}/course/${courseID}`, payload)
    .then(pluckData)

export let createShowNotesForLinkedService = (
  linkedServiceID: string,
  payload: ShowNotesPayload,
) =>
  axios
    .post(
      `${CREATE_SHOW_NOTES_API_URL}/linked-service/${linkedServiceID}`,
      payload,
    )
    .then(pluckData)

export let updateShowNotes = (id: string, payload: ShowNotesPayload) =>
  axios.put(`${CREATE_SHOW_NOTES_API_URL}/${id}`, payload).then(pluckData)

export let deleteShowNotes = (id: string) =>
  axios.delete(`${CREATE_SHOW_NOTES_API_URL}/${id}`)

export let addContentToCourses = (
  type: CreateContentTypes,
  id: string,
  courseIDs: string[],
) => {
  let baseUrl = contentTypeApiUrlMap.get(type)

  return !baseUrl
    ? Promise.reject(new Error('Invalid content type'))
    : axios.post(`${baseUrl}/${id}/add-to-courses`, { courseIDs })
}

export let removeContentFromCourses = (
  type: CreateContentTypes,
  id: string,
  courseIDs: string[],
) => {
  let baseUrl = contentTypeApiUrlMap.get(type)

  return !baseUrl
    ? Promise.reject(new Error('Invalid content type'))
    : axios.delete(`${baseUrl}/${id}/remove-from-courses`, {
        data: { courseIDs },
      })
}

// Quiz Library
export let getQuizLibrary = () =>
  axios.get<QuizLibraryItem[]>(`${CREATE_QUIZ_API_URL}-library`).then(pluckData)

export let createQuizByLinkedServiceID = (
  linkedServiceID: string,
  payload: { name: string },
) =>
  axios
    .post<{
      id: string
    }>(`${CREATE_QUIZ_API_URL}/linked-service/${linkedServiceID}`, payload)
    .then(pluckData)

export let createQuizByCourseID = (
  courseID: string,
  payload: { name: string },
) =>
  axios
    .post<{
      id: string
    }>(`${CREATE_QUIZ_API_URL}/course/${courseID}`, payload)
    .then(pluckData)

export let updateQuizMeta = (id: string, payload: { name: string }) =>
  axios.put(`${CREATE_QUIZ_API_URL}/${id}`, payload).then(pluckData)

export let deleteQuizLibraryItem = (id: string) =>
  axios.delete(`${CREATE_QUIZ_API_URL}/${id}`)

export let getSelectedQuiz = (quizID: string) =>
  axios.get<QuizV2>(`${CREATE_QUIZ_API_URL}/${quizID}`).then(pluckData)

export let getQuestionsByQuizID = (quizID: string) =>
  axios
    .get<QuestionData[]>(`${CREATE_QUIZ_API_URL}/${quizID}/questions`)
    .then(pluckData)

// Accreditations
export let fetchAccreditations = () =>
  axios
    .get<CourseAccreditationResponse[]>(`${CREATE_ACCREDITATIONS_API_URL}s`)
    .then(pluckData)

export let addCourseAccreditation = (
  courseID: string,
  type: CourseAccreditationType,
  accreditation: { courseCode: string },
) =>
  axios
    .post<CourseAccreditationResponse>(
      `${CREATE_ACCREDITATIONS_API_URL}/course/${courseID}`,
      { [type]: accreditation },
    )
    .then(pluckData)

export let updateCourseAccreditation = (
  courseID: string,
  accreditationID: string,
  type: CourseAccreditationType,
  accreditation: { courseCode: string },
) =>
  axios
    .put<CourseAccreditationResponse>(
      `${CREATE_ACCREDITATIONS_API_URL}/${accreditationID}/course/${courseID}`,
      { [type]: accreditation },
    )
    .then(pluckData)

export let removeCourseAccreditation = (
  courseID: string,
  accreditationID: string,
) =>
  axios.delete(
    `${CREATE_ACCREDITATIONS_API_URL}/${accreditationID}/course/${courseID}`,
  )

// Instructors
export let fetchCourseInstructors = () =>
  axios
    .get<InstructorWithCourses[]>(`${CREATE_INSTRUCTOR_API_URL}s`)
    .then(pluckData)

export let createCourseInstructor = (
  linkedServiceID: string,
  courseID: string,
  payload: { name: string; credentials?: string },
) =>
  axios
    .post<InstructorWithCourses>(
      `${CREATE_INSTRUCTOR_API_URL}/linked-service/${linkedServiceID}/course/${courseID}`,
      payload,
    )
    .then(pluckData)

export let updateCourseInstructor = (
  instructorID: string,
  payload: { name: string; credentials?: string },
) =>
  axios
    .put<InstructorWithCourses>(
      `${CREATE_INSTRUCTOR_API_URL}/${instructorID}`,
      payload,
    )
    .then(pluckData)

export let addInstructorToCourse = (instructorID: string, courseID: string) =>
  axios.post(`${CREATE_INSTRUCTOR_API_URL}/${instructorID}/add-to-courses`, {
    courseIDs: [courseID],
  })

export let removeInstructorFromCourse = (
  instructorID: string,
  courseID: string,
) =>
  axios.post(
    `${CREATE_INSTRUCTOR_API_URL}/${instructorID}/remove-from-courses`,
    {
      courseIDs: [courseID],
    },
  )

export let updateInstructorSequence = (
  courseID: string,
  instructors: { instructorID: string; sequence: number }[],
) =>
  axios.put(`${CREATE_INSTRUCTOR_API_URL}s/course/${courseID}/sequence`, {
    instructors,
  })

// Series
export let getSeries = () =>
  axios.get<SeriesResponse[]>(CREATE_SERIES_API_URL).then(pluckData)

export let getSeriesByID = (id: string) =>
  axios.get<SeriesResponse>(`${CREATE_SERIES_API_URL}/${id}`).then(pluckData)

export let createSeries = (linkedServiceID: string, payload: SeriesPayload) =>
  axios
    .post<SeriesResponse>(
      `${CREATE_SERIES_API_URL}/linked-service/${linkedServiceID}`,
      payload,
    )
    .then(pluckData)

export let updateSeries = (id: string, payload: SeriesPayload) =>
  axios
    .put<SeriesResponse>(`${CREATE_SERIES_API_URL}/${id}`, payload)
    .then(pluckData)

export let updateSeriesImage = (
  id: string,
  payload: UpdateSeriesImagePayload,
) =>
  axios
    .put<SeriesResponse>(`${CREATE_SERIES_API_URL}/${id}/image`, payload)
    .then(pluckData)

export let getSeriesImageUploadUrl = (
  id: string,
  type: SeriesImageType,
  contentType: string,
): Promise<{ url: string }> =>
  axios
    .put<string>(`${CREATE_SERIES_API_URL}/${id}/image`, { type, contentType })
    .then(pluckData)
    .then((url) => ({ url }))

export let updateSeriesPublicationStatus = (
  id: string,
  newPublicationStatus: 'published' | 'unpublished',
) =>
  axios
    .put(`${CREATE_SERIES_API_URL}/${id}/publish`, { newPublicationStatus })
    .then(pluckData)

export let updateSeriesEpisodeAssignments = (
  seriesID: string,
  assignments: { courseID: string; season: number; episode: number }[],
) =>
  axios
    .put(`${CREATE_SERIES_API_URL}/${seriesID}/assign-episodes`, {
      assignments,
    })
    .then(pluckData)

export let updateSeriesDisplayOrder = (
  linkedServiceID: string,
  assignments: { seriesID: string; sequence: number }[],
) =>
  axios
    .put(
      `/api/v3/create/series/linked-service/${linkedServiceID}/display-order`,
      {
        series: assignments.map((a) => ({
          seriesID: a.seriesID,
          sequence: a.sequence,
        })),
      },
    )
    .then(pluckData)

export let deleteSeries = (id: string) =>
  axios.delete(`${CREATE_SERIES_API_URL}/${id}`)
